/**
 * OUT OF CREDITS MODAL COMPONENT
 *
 * Shows when a user runs out of SMS, email, or AI credits.
 * Displays context-specific messaging and CTAs based on credit type and plan tier.
 *
 * Usage:
 *   <OutOfCreditsModal
 *     isOpen={showModal}
 *     onClose={() => setShowModal(false)}
 *     creditType="sms"
 *     used={250}
 *     limit={250}
 *     packRemaining={0}
 *     planTier="paid"
 *   />
 */

import { useNavigate } from 'react-router-dom';
import { X, MessageSquare, Mail, Sparkles } from 'lucide-react';
import { USAGE_LIMITS } from '@prosperia/shared/subscriptions';

const CREDIT_CONFIG = {
  sms: {
    icon: MessageSquare,
    label: 'SMS',
    labelPlural: 'SMS messages',
    gradient: 'from-green-500 to-emerald-600',
    hoverGradient: 'hover:from-green-600 hover:to-emerald-700',
    barColor: 'bg-red-500',
    freePlanLimit: USAGE_LIMITS.FREE.sms_messages,
    paidPlanLimit: USAGE_LIMITS.PAID.sms_messages,
  },
  email: {
    icon: Mail,
    label: 'Email',
    labelPlural: 'emails',
    gradient: 'from-blue-500 to-cyan-600',
    hoverGradient: 'hover:from-blue-600 hover:to-cyan-700',
    barColor: 'bg-red-500',
    freePlanLimit: USAGE_LIMITS.FREE.email_messages,
    paidPlanLimit: USAGE_LIMITS.PAID.email_messages,
  },
  ai: {
    icon: Sparkles,
    label: 'AI',
    labelPlural: 'AI credits',
    gradient: 'from-purple-500 to-violet-600',
    hoverGradient: 'hover:from-purple-600 hover:to-violet-700',
    barColor: 'bg-red-500',
    freePlanLimit: USAGE_LIMITS.FREE.ai_credits_monthly,
    paidPlanLimit: USAGE_LIMITS.PAID.ai_credits_monthly,
  },
};

const OutOfCreditsModal = ({ isOpen, onClose, creditType, used, limit, packRemaining, planTier }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const config = CREDIT_CONFIG[creditType] || CREDIT_CONFIG.sms;
  const IconComponent = config.icon;
  const isFree = planTier === 'free';
  // SMS requires Pro; email and AI packs are available to all tiers
  const canBuyPacks = creditType !== 'sms' || !isFree;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handlePrimaryAction = () => {
    onClose();
    if (canBuyPacks) {
      navigate('/settings/billing#packs');
    } else {
      navigate('/settings/subscription');
    }
  };

  const handlePrimaryKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePrimaryAction();
    }
  };

  const handleCloseKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  };

  const handleDismissKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={`Out of ${config.label} Credits`}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className={`p-6 text-white bg-gradient-to-r ${config.gradient}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 rounded-full p-2">
                <IconComponent className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  Out of {config.label} Credits
                </h3>
                <p className="text-sm opacity-90 mt-1">
                  {canBuyPacks ? 'Purchase more to continue' : 'Upgrade to keep sending'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              onKeyDown={handleCloseKeyDown}
              className="text-white hover:text-primary transition-colors"
              aria-label="Close modal"
              tabIndex={0}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Usage bar */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-secondary">Current Usage</span>
              <span className="text-sm font-semibold text-primary">
                {used.toLocaleString()} / {limit.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`${config.barColor} h-2 rounded-full w-full`} />
            </div>
            {packRemaining > 0 && (
              <p className="text-xs text-amber-600 mt-2">
                {packRemaining.toLocaleString()} pack credits also used
              </p>
            )}
            {packRemaining === 0 && (
              <p className="text-xs text-muted mt-2">
                No pack credits remaining
              </p>
            )}
          </div>

          {/* Message */}
          <div className="mb-6">
            {canBuyPacks ? (
              <p className="text-secondary">
                You&apos;ve used all {limit.toLocaleString()} included {config.labelPlural} this month
                and have no pack credits remaining. Purchase additional packs to continue.
              </p>
            ) : (
              <p className="text-secondary">
                Your free plan includes {config.freePlanLimit.toLocaleString()} {config.labelPlural} per month.
                Upgrade to Pro to unlock SMS and purchase additional packs.
              </p>
            )}
          </div>

          {/* CTA buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              onKeyDown={handleDismissKeyDown}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-secondary font-medium hover:bg-gray-50 transition-colors"
              aria-label="Dismiss and close modal"
              tabIndex={0}
            >
              Maybe Later
            </button>
            <button
              onClick={handlePrimaryAction}
              onKeyDown={handlePrimaryKeyDown}
              className={`flex-1 px-4 py-2 text-white rounded-md font-medium transition-colors bg-gradient-to-r ${config.gradient} ${config.hoverGradient}`}
              aria-label={canBuyPacks ? 'Buy more credit packs' : 'Upgrade to Pro plan'}
              tabIndex={0}
            >
              {canBuyPacks ? 'Buy More Packs' : 'Upgrade to Pro'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutOfCreditsModal;
